import { EditNote } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { API } from "config/api";
import { API_ROUTES } from "config/apiRoutes";
import {
  useAddFeedbackAmendmentRequestMutation,
  useAddStudentStatementMutation,
  useGetFeedbackAmendmentRequestsQuery,
} from "graphql/__generated__/hooks";
import { NotificationSeverity } from "helpers/constants";
import { useAuthUser } from "hooks/useAuthUser";
import { useNotifications } from "hooks/useNotifications";
import { FC, useState } from "react";
import { FeedbackStatus, FeedbackType } from "types/feedback";
import { ROLES } from "types/user";

type Decision = "APPROVED" | "DENIED";

const STATUS_COLOR: Record<string, "default" | "success" | "error" | "warning"> = {
  PENDING: "warning",
  APPROVED: "success",
  DENIED: "error",
};

const STATUS_LABEL: Record<string, string> = {
  PENDING: "Pending review",
  APPROVED: "Approved",
  DENIED: "Denied",
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString() : "";

const apiMessage = (error: unknown, fallback: string) => {
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message || fallback;
};

export const AmendmentRequests: FC<{
  feedback: FeedbackType;
  onChanged?: () => void;
}> = ({ feedback, onChanged }) => {
  const { user, role } = useAuthUser();
  const { notify } = useNotifications();
  const { data, refetch } = useGetFeedbackAmendmentRequestsQuery({
    variables: { feedbackId: feedback.feedback_id },
    fetchPolicy: "network-only",
  });
  const [addRequest, { loading: submitting }] =
    useAddFeedbackAmendmentRequestMutation();
  const [addStatement, { loading: stating }] = useAddStudentStatementMutation();

  const [requestOpen, setRequestOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [decision, setDecision] = useState<{
    id: string;
    status: Decision;
  } | null>(null);
  const [decisionNote, setDecisionNote] = useState("");
  const [statementFor, setStatementFor] = useState<string | null>(null);
  const [statement, setStatement] = useState("");

  const requests = data?.feedback_amendment_request ?? [];
  const isOwner =
    role === ROLES.STUDENT && feedback.student_id === user?.user_id;
  const isStaff =
    role === ROLES.ADMIN || role === ROLES.DIRECTOR || role === ROLES.MANAGER;
  const hasPending = requests.some((r) => r.status === "PENDING");
  const canRequest =
    isOwner && feedback.status === FeedbackStatus.DONE && !hasPending;

  if (!requests.length && !canRequest && !feedback.record_correction) return null;

  const submitRequest = async () => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    try {
      const created = await addRequest({
        variables: { feedbackId: feedback.feedback_id, reason: trimmed },
      });
      const id =
        created.data?.insert_feedback_amendment_request_one
          ?.amendment_request_id;
      setRequestOpen(false);
      setReason("");
      if (id) {
        try {
          await API.post(API_ROUTES.FEEDBACK_AMENDMENT_REQUESTS.NOTIFY(id));
        } catch {
          notify({
            message:
              "Request saved, but your program could not be emailed. Tell a director directly.",
            severity: NotificationSeverity.warning,
          });
        }
      }
      await refetch();
      onChanged?.();
      notify({
        message: "Correction request sent to your program.",
        severity: NotificationSeverity.success,
      });
    } catch {
      notify({
        message: "Couldn't send the correction request. Try again.",
        severity: NotificationSeverity.error,
      });
    }
  };

  const submitDecision = async () => {
    if (!decision) return;
    try {
      await API.post(API_ROUTES.FEEDBACK_AMENDMENT_REQUESTS.DECIDE(decision.id), {
        status: decision.status,
        decision_note: decisionNote.trim(),
      });
      setDecision(null);
      setDecisionNote("");
      await refetch();
      onChanged?.();
      notify({
        message:
          decision.status === "APPROVED"
            ? "Correction written onto the record."
            : "Decision saved. The learner can add a statement.",
        severity: NotificationSeverity.success,
      });
    } catch (error) {
      notify({
        message: apiMessage(error, "Couldn't save the decision. Try again."),
        severity: NotificationSeverity.error,
      });
    }
  };

  const submitStatement = async () => {
    const trimmed = statement.trim();
    if (!statementFor || !trimmed) return;
    try {
      await addStatement({
        variables: { id: statementFor, statement: trimmed },
      });
      setStatementFor(null);
      setStatement("");
      await refetch();
      onChanged?.();
      notify({
        message: "Your statement is now part of this record.",
        severity: NotificationSeverity.success,
      });
    } catch {
      notify({
        message: "Couldn't save the statement. Try again.",
        severity: NotificationSeverity.error,
      });
    }
  };

  return (
    <Box my={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">Correction requests</Typography>
        {canRequest && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditNote />}
            onClick={() => setRequestOpen(true)}
          >
            Request a correction
          </Button>
        )}
      </Stack>

      {feedback.record_correction && (
        <Alert severity="success" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2">Record correction</Typography>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {feedback.record_correction}
          </Typography>
        </Alert>
      )}

      {!requests.length && (
        <Typography variant="body2" color="text.secondary">
          If something in this record is inaccurate or misleading, you can ask
          your program to correct it.
        </Typography>
      )}

      <Stack spacing={1.5}>
        {requests.map((r) => (
          <Box
            key={r.amendment_request_id}
            sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
            >
              <Typography variant="body2" color="text.secondary">
                {r.requester?.name || "Learner"} · {formatDate(r.created_at)}
              </Typography>
              <Chip
                size="small"
                label={STATUS_LABEL[r.status] || r.status}
                color={STATUS_COLOR[r.status] || "default"}
              />
            </Stack>
            <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
              {r.reason}
            </Typography>
            {r.status !== "PENDING" && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {STATUS_LABEL[r.status]}
                {r.decider?.name ? ` by ${r.decider.name}` : ""}
                {r.decided_at ? ` on ${formatDate(r.decided_at)}` : ""}
                {r.decision_note ? `: ${r.decision_note}` : ""}
              </Typography>
            )}
            {r.student_statement && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">Learner's statement</Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                  {r.student_statement}
                </Typography>
              </Box>
            )}
            {isOwner && r.status === "DENIED" && !r.student_statement && (
              <Box mt={1}>
                <Button
                  size="small"
                  onClick={() => setStatementFor(r.amendment_request_id)}
                >
                  Add a statement to the record
                </Button>
              </Box>
            )}
            {isStaff && r.status === "PENDING" && (
              <Stack direction="row" spacing={1} justifyContent="flex-end" mt={1}>
                <Button
                  size="small"
                  color="error"
                  onClick={() =>
                    setDecision({ id: r.amendment_request_id, status: "DENIED" })
                  }
                >
                  Deny
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() =>
                    setDecision({
                      id: r.amendment_request_id,
                      status: "APPROVED",
                    })
                  }
                >
                  Approve
                </Button>
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      <Dialog
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Request a correction</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Describe what is inaccurate or misleading and what it should say.
            Your program director will review the request.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The observation date should be March 4, not March 14."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRequestOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!reason.trim() || submitting}
            onClick={submitRequest}
          >
            Send request
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(decision)}
        onClose={() => setDecision(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {decision?.status === "APPROVED"
            ? "Approve correction request"
            : "Deny correction request"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {decision?.status === "APPROVED"
              ? "This note is written onto the education record as the correction. The learner will see it."
              : "Explain why the record stays as is. The learner will see this note and may add a statement to the record."}
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={decisionNote}
            onChange={(e) => setDecisionNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecision(null)}>Cancel</Button>
          <Button
            variant="contained"
            color={decision?.status === "APPROVED" ? "primary" : "error"}
            disabled={!decisionNote.trim()}
            onClick={submitDecision}
          >
            {decision?.status === "APPROVED" ? "Approve and correct the record" : "Deny"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(statementFor)}
        onClose={() => setStatementFor(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add a statement</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            This statement stays with the record and is included whenever the
            record is released.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatementFor(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!statement.trim() || stating}
            onClick={submitStatement}
          >
            Save statement
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
